import type {
  ConversationBrief,
  ConversationMessage,
  ConversationQuestion,
  ConversationStaleness
} from '@/api/conversation/conversation'
import { decodeConversationFrame } from '@/api/conversation/stream'
import type { ConversationFrame } from '@/api/conversation/stream'
import { briefChangeLabel, outcomeLabel, questionEventLabel } from '../eventCopy'

export type ConversationConnection = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'rebootstrapping' | 'unsupported'

export type ConversationAttempt = {
  executionId: number
  stepKey: string
  attempt: number
  attemptEpoch: string
  lastSequence: number
  text: string
}

export type ConversationExecutionState = {
  runId?: number
  status?: 'queued' | 'running' | 'waiting_for_input' | 'succeeded' | 'failed' | 'cancelled'
  stage?: string
  summary?: string
  // The execution graph's ordered step keys, from whichever frame last carried them.
  // A lifecycle frame carries none, so an absent list leaves the previous one alone:
  // blanking it would drop a running deck back to an indeterminate bar mid-build.
  steps?: Array<string>
  retryable?: boolean
}

type StatusFrame = Extract<ConversationFrame, { type: 'status' }>
type QuestionFrame = Extract<ConversationFrame, { type: 'question' }>
type ConversationTask = Extract<ConversationFrame, { type: 'snapshot' }>['tasks'][number]
type IntakeTerminalStates = Record<number, Pick<ConversationTask, 'intakeStatus' | 'intakeStage'>>

// The status outcomes that earn a transcript card. Both are terminal refusals carrying
// the server's own sentence, which is the reader's only account of what happened. Typed
// against the frame's own union so a typo here is a compile error rather than a rule that
// silently matches nothing.
const cardedStatusOutcomes: Array<NonNullable<StatusFrame['outcome']>> = ['unsupported-task', 'capability-unavailable']

// The question transitions that earn a transcript card, being the ones no other surface
// reports. See applyQuestion for why `ask` does not.
const cardedQuestionTransitions: Array<NonNullable<QuestionFrame['transition']>> = [
  'accepted',
  'superseded',
  'abandoned'
]

export type ConversationChange =
  | { eventId: number; kind: 'brief'; label: string; briefId: number }
  | {
      eventId: number
      kind: 'artifact'
      label: string
      // A canvas refine has no execution behind it, so executionId is absent on the change
      // it produces. originMessageId names the instruction that caused the change, and is
      // what anchors an execution-less one beside its own turn.
      executionId?: number
      originMessageId?: number
      artifactType: 'proposal' | 'proposal-section' | 'presentation' | 'presentation-slide' | 'document'
      proposalId?: number
      sectionId?: number
      presentationId?: number
      slideId?: number
      versionId?: number
      documentId?: number
      // What changed and how much of it. words and previousWords are absolute counts,
      // not a signed delta: a delta cannot tell a created artifact from one whose
      // length did not move, and previousWords is absent where the producer could not
      // read the superseded content.
      targetName?: string
      words?: number
      previousWords?: number
    }
  | {
      eventId: number
      kind: 'question'
      label: string
      briefId: number
      runId: number
      // The transition lets the presentation layer distinguish a durable history fact
      // from something that deserves its own transcript row. The label alone is copy and
      // must never become state authority.
      transition: NonNullable<QuestionFrame['transition']>
    }
  // detail carries the server's own sentence, which some outcomes need beyond their
  // label: a refusal has to say what the assistant can do instead.
  | { eventId: number; kind: 'status'; label: string; detail?: string; executionId?: number; runId?: number }
  | { eventId: number; kind: 'error'; label: string; executionId?: number; runId?: number }

export type ConversationState = {
  connection: ConversationConnection
  protocolRebootstrapCount: 0 | 1
  projectId?: number
  threadId?: number
  highWaterEventId: number
  tailAfterEventId: number
  rebased: boolean
  eventsTruncated: boolean
  olderMessages: Array<ConversationMessage>
  olderTasks: Extract<ConversationFrame, { type: 'snapshot' }>['tasks']
  // A terminal run may leave the active snapshot before its older page arrives.
  // Keep its status independently of loaded messages so late pages cannot revive it.
  intakeTerminalStates: IntakeTerminalStates
  liveMessages: Array<ConversationMessage>
  earliestCursor?: string
  hasOlder: boolean
  tasks: Extract<ConversationFrame, { type: 'snapshot' }>['tasks']
  briefs: Array<ConversationBrief>
  questions: Array<ConversationQuestion>
  // A question run that spent its ask budget with slots still missing. The task cannot
  // execute until they are supplied, so the transcript has to say which they are.
  exhausted: Array<{ runId: number; briefId: number; remainingSlotKeys: Array<string> }>
  outputs: Array<ConversationStaleness>
  attempts: Record<string, ConversationAttempt>
  executionStates: Record<number, ConversationExecutionState>
  // Authoritative creation time comes from snapshot task state. A persisted event's
  // occurrence time is next, and local receipt time remains only for older servers.
  // Keeping this outside executionStates lets it survive the refresh that follows a
  // committed step without making a timestamp-only execution look live.
  //
  // It is kept apart from executionStates rather than carried through it, because an entry
  // holding a stamp and no status would read as live and spin a bar for a finished run.
  executionStartedAt: Record<number, number>
  changes: Array<ConversationChange>
  seenEventIds: Array<number>
  briefConflicts: Record<number, { expectedVersion: number; actualVersion?: number }>
  snapshotRefreshRequired: boolean
  pendingCanonicalMessageIds: Array<number>
  invalidatedBriefIds: Array<number>
}

export const initialConversationState: ConversationState = {
  connection: 'idle',
  protocolRebootstrapCount: 0,
  highWaterEventId: 0,
  tailAfterEventId: 0,
  rebased: false,
  eventsTruncated: false,
  olderMessages: [],
  olderTasks: [],
  intakeTerminalStates: {},
  liveMessages: [],
  hasOlder: false,
  tasks: [],
  briefs: [],
  questions: [],
  exhausted: [],
  outputs: [],
  attempts: {},
  executionStates: {},
  executionStartedAt: {},
  changes: [],
  seenEventIds: [],
  briefConflicts: {},
  snapshotRefreshRequired: false,
  pendingCanonicalMessageIds: [],
  invalidatedBriefIds: []
}

export type ConversationAction =
  | { type: 'connect-started' }
  | { type: 'stream-connected' }
  | { type: 'stream-disconnected' }
  | { type: 'protocol-mismatch' }
  // receivedAtTsMs is passed in rather than read from the clock inside the reducer, which
  // has to stay a pure function of (state, action): it runs twice under StrictMode, and a
  // clock read there is also unfakeable from a test.
  | { type: 'frame-received'; frame: ConversationFrame; receivedAtTsMs?: number }
  | {
      type: 'older-page-loaded'
      messages: Array<ConversationMessage>
      tasks?: ConversationState['tasks']
      hasOlder: boolean
      earliestCursor?: string
    }
  | { type: 'brief-correction-started'; briefId: number; expectedVersion: number }
  | { type: 'brief-correction-conflict'; briefId: number; actualVersion: number }
  | { type: 'brief-correction-resolved'; briefId: number }

function attemptKey(executionId: number, stepKey: string): string {
  return `${executionId}:${stepKey}`
}

function addSeen(state: ConversationState, eventId: number): ConversationState {
  if (state.seenEventIds.includes(eventId)) return state
  return { ...state, seenEventIds: [...state.seenEventIds, eventId] }
}

function withChange(state: ConversationState, change: ConversationChange): ConversationState {
  return { ...state, changes: [...state.changes, change] }
}

function isTerminalIntakeStatus(status: string | undefined): status is 'failed' | 'cancelled' | 'succeeded' {
  return status === 'failed' || status === 'cancelled' || status === 'succeeded'
}

function withIntakeTerminal(task: ConversationTask, terminalStates: IntakeTerminalStates): ConversationTask {
  const terminal = terminalStates[task.intakeRunId]
  return terminal === undefined ? task : { ...task, ...terminal }
}

function applySnapshot(
  state: ConversationState,
  frame: Extract<ConversationFrame, { type: 'snapshot' }>,
  receivedAtTsMs?: number
): ConversationState {
  const scopeChanged =
    state.projectId !== undefined && (state.projectId !== frame.projectId || state.threadId !== frame.threadId)
  const liveIDs = new Set(frame.messages.map((message) => message.messageId))
  const snapshotIntakes = new Map(frame.tasks.map((task) => [task.intakeRunId, task]))
  const executionStartedAt: Record<number, number> = scopeChanged ? {} : { ...state.executionStartedAt }
  const intakeTerminalStates: IntakeTerminalStates = scopeChanged ? {} : { ...state.intakeTerminalStates }
  for (const task of frame.tasks) {
    if (
      task.executionId !== null &&
      task.executionId !== undefined &&
      task.executionCreatedAtTsMs !== null &&
      task.executionCreatedAtTsMs !== undefined
    ) {
      executionStartedAt[task.executionId] = task.executionCreatedAtTsMs
    }
    if (isTerminalIntakeStatus(task.intakeStatus)) {
      intakeTerminalStates[task.intakeRunId] = { intakeStatus: task.intakeStatus, intakeStage: task.intakeStage }
    } else {
      delete intakeTerminalStates[task.intakeRunId]
    }
  }
  const stateWithoutCursor = { ...state }
  delete stateWithoutCursor.earliestCursor
  let next: ConversationState = {
    ...stateWithoutCursor,
    connection: 'live',
    protocolRebootstrapCount: scopeChanged ? 0 : state.protocolRebootstrapCount,
    projectId: frame.projectId,
    threadId: frame.threadId,
    highWaterEventId: frame.highWaterEventId,
    tailAfterEventId: frame.tailAfterEventId,
    rebased: frame.rebased,
    eventsTruncated: frame.eventsTruncated,
    olderMessages: scopeChanged ? [] : state.olderMessages.filter((message) => !liveIDs.has(message.messageId)),
    olderTasks: scopeChanged
      ? []
      : state.olderTasks
          .filter((task) => !liveIDs.has(task.originMessageId))
          .map((task) => {
            const current = snapshotIntakes.get(task.intakeRunId)
            return current === undefined
              ? withIntakeTerminal(task, intakeTerminalStates)
              : { ...task, intakeStatus: current.intakeStatus, intakeStage: current.intakeStage }
          }),
    intakeTerminalStates,
    liveMessages: [...frame.messages],
    ...(frame.earliestCursor === undefined ? {} : { earliestCursor: frame.earliestCursor }),
    hasOlder: frame.hasOlder,
    tasks: [...frame.tasks],
    briefs: [...frame.briefs],
    questions: [...frame.questions],
    exhausted: [],
    outputs: [...frame.outputs],
    attempts: {},
    executionStates: {},
    executionStartedAt,
    changes: [],
    seenEventIds: [],
    briefConflicts: scopeChanged ? {} : state.briefConflicts,
    snapshotRefreshRequired: false,
    pendingCanonicalMessageIds: [],
    invalidatedBriefIds: []
  }
  for (const embedded of frame.events) {
    next = applyFrame(next, decodeConversationFrame(embedded), true, receivedAtTsMs)
  }
  return addSeen(next, frame.eventId)
}

function applyStatus(
  state: ConversationState,
  frame: Extract<ConversationFrame, { type: 'status' }>,
  embedded: boolean,
  receivedAtTsMs?: number
): ConversationState {
  let next = state
  // Current snapshot tasks outrank replayed events. A retained older task has no
  // such authority when its inactive root is omitted, so replay must recover its
  // terminal status too. Unknown live runs ask for their origin via a fresh snapshot.
  if (frame.executionId === undefined && frame.runId !== undefined) {
    const snapshotTask = state.tasks.find((task) => task.intakeRunId === frame.runId)
    const knownTask = snapshotTask ?? state.olderTasks.find((task) => task.intakeRunId === frame.runId)
    const applyIntake = !embedded || snapshotTask === undefined
    if (applyIntake && isTerminalIntakeStatus(frame.status) && (knownTask !== undefined || frame.stage === 'intake')) {
      next = {
        ...next,
        intakeTerminalStates: {
          ...next.intakeTerminalStates,
          [frame.runId]: {
            intakeStatus: frame.status,
            intakeStage: frame.stage ?? knownTask?.intakeStage ?? 'intake'
          }
        }
      }
    }
    if (applyIntake && knownTask !== undefined) {
      const updateTask = (task: ConversationState['tasks'][number]) =>
        task.intakeRunId !== frame.runId
          ? task
          : withIntakeTerminal(
              {
                ...task,
                ...(frame.status === undefined ? {} : { intakeStatus: frame.status }),
                ...(frame.stage === undefined ? {} : { intakeStage: frame.stage })
              },
              next.intakeTerminalStates
            )
      next = {
        ...next,
        tasks: state.tasks.map(updateTask),
        olderTasks: state.olderTasks.map(updateTask)
      }
    } else if (!embedded && knownTask === undefined && frame.stage === 'intake') {
      next = { ...next, snapshotRefreshRequired: true }
    }
  }
  if (frame.executionId !== undefined) {
    const current = state.executionStates[frame.executionId] ?? {}
    const live = frame.status === 'queued' || frame.status === 'running'
    const updated: ConversationExecutionState = {
      ...current,
      ...(frame.runId === undefined ? {} : { runId: frame.runId }),
      ...(frame.status === undefined ? {} : { status: frame.status }),
      ...(frame.stage === undefined ? {} : { stage: frame.stage }),
      ...(frame.summary === undefined ? {} : { summary: frame.summary }),
      // An empty trail means the same as an absent one, which is what the validator on
      // both sides says. Taking it literally would blank a running deck's trail and drop
      // the indicator back to an indeterminate bar mid-build.
      ...(frame.steps === undefined || frame.steps.length === 0 ? {} : { steps: [...frame.steps] }),
      ...(frame.retryable === undefined ? {} : { retryable: frame.retryable })
    }
    next = { ...next, executionStates: { ...next.executionStates, [frame.executionId]: updated } }
    const startedAtTsMs = frame.occurredAtTsMs ?? receivedAtTsMs
    if (live && next.executionStartedAt[frame.executionId] === undefined && startedAtTsMs !== undefined) {
      next = {
        ...next,
        executionStartedAt: { ...next.executionStartedAt, [frame.executionId]: startedAtTsMs }
      }
    }
    if (frame.status === 'failed' || frame.status === 'cancelled' || frame.status === 'succeeded') {
      next = {
        ...next,
        attempts: Object.fromEntries(
          Object.entries(next.attempts).filter(([, attempt]) => attempt.executionId !== frame.executionId)
        )
      }
    }
  }
  // A card is read from the frame's outcome alone, never from its status or its stage.
  // Those two are the progress indicator's material: a status repeats what the indicator
  // says while it happens and then says it again forever, and a stage arrives on every
  // step boundary of every run, so carding it would put the whole graph in the transcript
  // of every task. The outcome is where the terminal, act-on-able states live, and even
  // there only cardedStatusOutcomes qualify. Unrecognised copy still yields undefined, so
  // an unmapped token cannot leak its identifier.
  const label =
    frame.outcome !== undefined && cardedStatusOutcomes.includes(frame.outcome)
      ? outcomeLabel(frame.outcome)
      : undefined
  if (label !== undefined) {
    next = withChange(next, {
      eventId: frame.eventId,
      kind: 'status',
      label,
      ...(frame.message === undefined || frame.message.trim() === '' ? {} : { detail: frame.message }),
      ...(frame.executionId === undefined ? {} : { executionId: frame.executionId }),
      ...(frame.runId === undefined ? {} : { runId: frame.runId })
    })
  }
  return next
}

function applyQuestion(
  state: ConversationState,
  frame: Extract<ConversationFrame, { type: 'question' }>,
  embedded: boolean
): ConversationState {
  const terminal =
    frame.transition === 'accepted' ||
    frame.transition === 'superseded' ||
    frame.transition === 'budget-exhausted' ||
    frame.transition === 'abandoned'
  let questions = terminal
    ? state.questions.filter((question) => question.runId !== frame.runId || question.slotKey !== frame.slotKey)
    : state.questions
  if (!terminal && frame.kind !== undefined && frame.briefVersion !== undefined) {
    // The frame carries identifiers only, so anything it cannot express is taken from
    // the question the snapshot already delivered rather than blanked. An embedded
    // frame replays inside applySnapshot and sets no refresh flag, so a blank here
    // would silently drop the pre-check line and the truncation notice with nothing to
    // fetch them back.
    const known = questions.find((question) => question.runId === frame.runId && question.slotKey === frame.slotKey)
    questions = [
      ...questions.filter((question) => question.runId !== frame.runId || question.slotKey !== frame.slotKey),
      {
        runId: frame.runId,
        briefId: frame.briefId,
        briefVersion: frame.briefVersion,
        suspensionKey: frame.suspensionKey ?? '',
        ordinal: frame.ordinal ?? 0,
        slotKey: frame.slotKey,
        kind: frame.kind,
        prompt: frame.prompt ?? '',
        candidates: frame.candidates ?? [],
        artifactOptions: frame.artifactOptions ?? [],
        satisfied: known?.satisfied ?? [],
        candidatesTruncated: known?.candidatesTruncated ?? false,
        skippable: known?.skippable ?? false
      }
    ]
  }
  // Exhaustion is a state the user has to act on, not a passing event: it stays until the
  // run asks again or is dropped, and it is the only frame that names what is still needed.
  const exhausted =
    frame.transition === 'budget-exhausted'
      ? [
          ...state.exhausted.filter((entry) => entry.runId !== frame.runId),
          { runId: frame.runId, briefId: frame.briefId, remainingSlotKeys: [...(frame.remainingSlotKeys ?? [])] }
        ]
      : state.exhausted.filter((entry) => entry.runId !== frame.runId)
  // Slot keys are capability-registry identifiers, so this read as `source-material: ask`.
  // budget-exhausted carries no slot key at all and rendered as a bare `: budget-exhausted`.
  //
  // Only the transitions nothing else reports get a card. `ask` goes because the question
  // turn is on screen showing exactly that, and ConversationExhaustedCard reports
  // budget-exhausted with the controls to act on it.
  //
  // `accepted` stays. Answering is terminal, so the turn unmounts, and the answer survives
  // only as a user message carrying the bare text or the chosen file names. Without this
  // card the transcript reads as an utterance the reader never typed, with nothing saying
  // what it answered. A replaced answer appears in no turn either, and an abandoned
  // question would leave no account of why the run stopped.
  const transition = frame.transition
  const label =
    transition !== undefined && cardedQuestionTransitions.includes(transition)
      ? questionEventLabel(frame.slotKey, transition)
      : undefined
  const next =
    !embedded && (frame.transition === 'ask' || (frame.kind !== undefined && frame.briefVersion === undefined))
      ? { ...state, questions, exhausted, snapshotRefreshRequired: true }
      : { ...state, questions, exhausted }
  if (label === undefined || transition === undefined) return next
  return withChange(next, {
    eventId: frame.eventId,
    kind: 'question',
    label,
    briefId: frame.briefId,
    runId: frame.runId,
    transition
  })
}

function applyFrame(
  state: ConversationState,
  frame: ConversationFrame,
  embedded = false,
  receivedAtTsMs?: number
): ConversationState {
  if (frame.type !== 'snapshot') {
    if (state.seenEventIds.includes(frame.eventId)) return state
    if (
      !embedded &&
      (state.projectId === undefined ||
        state.threadId === undefined ||
        frame.projectId !== state.projectId ||
        frame.threadId !== state.threadId ||
        frame.eventId <= state.tailAfterEventId)
    ) {
      return state
    }
  }
  if (frame.type === 'snapshot') return applySnapshot(state, frame, receivedAtTsMs)
  let next = addSeen(state, frame.eventId)
  if (!embedded) next = { ...next, tailAfterEventId: frame.eventId }
  switch (frame.type) {
    case 'status':
      return applyStatus(next, frame, embedded, receivedAtTsMs)
    case 'question':
      return applyQuestion(next, frame, embedded)
    case 'brief-delta': {
      const invalidated = embedded
        ? next
        : {
            ...next,
            snapshotRefreshRequired: true,
            invalidatedBriefIds: [...new Set([...next.invalidatedBriefIds, frame.briefId])]
          }
      // A delta that names no slots has nothing to say. It read as a bare `Brief updated`
      // beside a button that opened the panel the correction was made in, so the card was
      // a round trip back to the reader's own last action. The brief still invalidates.
      const label = briefChangeLabel(frame.changedSlotKeys)
      if (label === undefined) return invalidated
      return withChange(invalidated, {
        eventId: frame.eventId,
        kind: 'brief',
        label,
        briefId: frame.briefId
      })
    }
    case 'content-start': {
      const key = attemptKey(frame.executionId, frame.stepKey)
      return {
        ...next,
        attempts: {
          ...next.attempts,
          [key]: {
            executionId: frame.executionId,
            stepKey: frame.stepKey,
            attempt: frame.attempt,
            attemptEpoch: frame.attemptEpoch,
            lastSequence: 0,
            text: ''
          }
        }
      }
    }
    case 'content-delta': {
      const key = attemptKey(frame.executionId, frame.stepKey)
      const current = next.attempts[key]
      if (current === undefined || current.attempt !== frame.attempt || current.attemptEpoch !== frame.attemptEpoch) {
        return next
      }
      if (frame.sequence <= current.lastSequence) return next
      if (frame.sequence !== current.lastSequence + 1) {
        const attempts = { ...next.attempts }
        delete attempts[key]
        return { ...next, attempts, snapshotRefreshRequired: true }
      }
      return {
        ...next,
        attempts: {
          ...next.attempts,
          [key]: { ...current, lastSequence: frame.sequence, text: current.text + frame.text }
        }
      }
    }
    case 'content-committed': {
      const messageIDs = frame.messageId === undefined ? (frame.messageIds ?? []) : [frame.messageId]
      return {
        ...next,
        ...(embedded
          ? {}
          : {
              snapshotRefreshRequired: true,
              pendingCanonicalMessageIds: [...new Set([...next.pendingCanonicalMessageIds, ...messageIDs])]
            }),
        attempts: Object.fromEntries(
          Object.entries(next.attempts).filter(([, attempt]) => attempt.executionId !== frame.executionId)
        )
      }
    }
    case 'artifact-change': {
      const label =
        frame.artifactType === 'proposal'
          ? 'Proposal created'
          : frame.artifactType === 'proposal-section'
            ? 'Proposal section updated'
            : frame.artifactType === 'presentation-slide'
              ? // Two producers emit this type: the regenerate-slide checkpoint and a canvas
                // refine, one per refined slide. Both only ever redraft an existing slide,
                // neither creates or deletes one, so the verb is still safe to be specific
                // about. `Presentation slide updated` said less and repeated the glyph
                // beside it. A third producer that did anything but redraft would have to
                // widen this back out.
                'Rewrote the slide'
              : frame.artifactType === 'document'
                ? 'Document updated'
                : 'Presentation updated'
      const changed = withChange(next, {
        eventId: frame.eventId,
        kind: 'artifact',
        label,
        ...(frame.executionId === undefined ? {} : { executionId: frame.executionId }),
        originMessageId: frame.originMessageId,
        artifactType: frame.artifactType,
        ...(frame.proposalId === undefined ? {} : { proposalId: frame.proposalId }),
        ...(frame.sectionId === undefined ? {} : { sectionId: frame.sectionId }),
        ...(frame.presentationId === undefined ? {} : { presentationId: frame.presentationId }),
        ...(frame.slideId === undefined ? {} : { slideId: frame.slideId }),
        ...(frame.versionId === undefined ? {} : { versionId: frame.versionId }),
        ...(frame.documentId === undefined ? {} : { documentId: frame.documentId }),
        ...(frame.targetName === undefined ? {} : { targetName: frame.targetName }),
        ...(frame.words === undefined ? {} : { words: frame.words }),
        ...(frame.previousWords === undefined ? {} : { previousWords: frame.previousWords })
      })
      // An execution-less change comes from a canvas refine, which emits no
      // content-committed, so this frame is the only word the client gets that the refine's
      // new messages exist. Execution-backed work already refreshes through
      // content-committed, and setting the flag here too would refetch the whole snapshot
      // twice for every commit. An embedded replay asks for nothing either, for the same
      // reason content-committed does not: the snapshot being replayed is already the
      // refetched truth, and a flag set from inside it would never come back down.
      return frame.executionId === undefined && !embedded ? { ...changed, snapshotRefreshRequired: true } : changed
    }
    case 'staleness': {
      const outputs = [
        ...next.outputs.filter((output) => output.messageId !== frame.messageId),
        {
          messageId: frame.messageId,
          state: frame.state,
          reasons: frame.reasons,
          ...(frame.briefId === undefined ? {} : { briefId: frame.briefId }),
          ...(frame.linkedVersion === undefined ? {} : { linkedVersion: frame.linkedVersion }),
          ...(frame.currentVersion === undefined ? {} : { currentVersion: frame.currentVersion })
        }
      ]
      return { ...next, outputs }
    }
    case 'error': {
      const withoutAttempt =
        frame.executionId === undefined
          ? next
          : {
              ...next,
              attempts: Object.fromEntries(
                Object.entries(next.attempts).filter(([, attempt]) => attempt.executionId !== frame.executionId)
              )
            }
      return withChange(withoutAttempt, {
        eventId: frame.eventId,
        kind: 'error',
        label: frame.message,
        ...(frame.executionId === undefined ? {} : { executionId: frame.executionId }),
        ...(frame.runId === undefined ? {} : { runId: frame.runId })
      })
    }
  }
}

export function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'connect-started':
      return { ...state, connection: state.connection === 'live' ? 'reconnecting' : 'connecting' }
    case 'stream-connected':
      return { ...state, connection: 'live' }
    case 'stream-disconnected':
      return { ...state, connection: state.connection === 'unsupported' ? 'unsupported' : 'reconnecting' }
    case 'protocol-mismatch':
      return state.protocolRebootstrapCount === 0
        ? { ...state, protocolRebootstrapCount: 1, connection: 'rebootstrapping' }
        : { ...state, connection: 'unsupported' }
    case 'frame-received':
      return applyFrame(state, action.frame, false, action.receivedAtTsMs)
    case 'older-page-loaded': {
      const liveIDs = new Set(state.liveMessages.map((message) => message.messageId))
      const older = new Map(state.olderMessages.map((message) => [message.messageId, message]))
      for (const message of action.messages) if (!liveIDs.has(message.messageId)) older.set(message.messageId, message)
      const intakeTerminalStates = { ...state.intakeTerminalStates }
      for (const task of action.tasks ?? []) {
        if (intakeTerminalStates[task.intakeRunId] === undefined && isTerminalIntakeStatus(task.intakeStatus)) {
          intakeTerminalStates[task.intakeRunId] = { intakeStatus: task.intakeStatus, intakeStage: task.intakeStage }
        }
      }
      const tasks = new Map(
        [...state.olderTasks, ...(action.tasks ?? [])].map(
          (task) =>
            [
              `${task.originMessageId}:${task.executionId ?? 0}`,
              withIntakeTerminal(task, intakeTerminalStates)
            ] as const
        )
      )
      const next = { ...state }
      delete next.earliestCursor
      return {
        ...next,
        intakeTerminalStates,
        olderMessages: [...older.values()].sort(
          (left, right) => left.createdAtTsMs - right.createdAtTsMs || left.messageId - right.messageId
        ),
        olderTasks: [...tasks.values()].filter(
          (task) => older.has(task.originMessageId) && !liveIDs.has(task.originMessageId)
        ),
        hasOlder: action.hasOlder,
        ...(!action.hasOlder || action.earliestCursor === undefined ? {} : { earliestCursor: action.earliestCursor })
      }
    }
    case 'brief-correction-started':
      return {
        ...state,
        briefConflicts: {
          ...state.briefConflicts,
          [action.briefId]: { expectedVersion: action.expectedVersion }
        }
      }
    case 'brief-correction-conflict': {
      const current = state.briefConflicts[action.briefId]
      if (current === undefined) return state
      return {
        ...state,
        briefConflicts: {
          ...state.briefConflicts,
          [action.briefId]: { ...current, actualVersion: action.actualVersion }
        }
      }
    }
    case 'brief-correction-resolved': {
      const conflicts = { ...state.briefConflicts }
      delete conflicts[action.briefId]
      return { ...state, briefConflicts: conflicts }
    }
    default: {
      const exhaustive: never = action
      return exhaustive
    }
  }
}
